import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Phone, MapPin, Send, Clock, MessageCircle } from 'lucide-react';
import Header from './components/Header21';
import Footer from './components/Footer21';

import { devLog } from '../../../utils/devLog';

const Contact21 = () => {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: '',
    });
    const [submitted, setSubmitted] = useState(false);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        devLog.log('Contact form submitted:', formData);
        setSubmitted(true);
        setTimeout(() => {
            setSubmitted(false);
            setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
        }, 3000);
    };

    return (
        <>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;0,700;1,400;1,500&family=DM+Sans:wght@300;400;500;600;700&display=swap');
            `}</style>
            <div style={{ fontFamily: "'DM Sans', -apple-system, sans-serif" }} className="min-h-screen bg-[#0A0A0A]">
                <Header />

                {/* Hero Section */}
                <section className="py-16 lg:py-24 border-b border-[#2A2A2A]">
                    <div className="container mx-auto px-4 lg:px-8">
                        <div className="max-w-3xl mx-auto text-center">
                            <p className="text-[#C9A96E] text-sm tracking-[0.3em] uppercase mb-4" style={{ fontFamily: "'DM Sans', sans-serif" }}>Get In Touch</p>
                            <h1 style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }} className="text-4xl lg:text-6xl font-light text-[#F5F0EB] mb-6 tracking-wide">
                                Contact Us
                            </h1>
                            <p className="text-[#8A8A7A] text-lg leading-relaxed">
                                We would love to hear from you. Reach out for bespoke inquiries, collaboration, or any assistance.
                            </p>
                        </div>
                    </div>
                </section>

                <div className="container mx-auto px-4 lg:px-8 py-12 lg:py-20">
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12">
                        {/* Contact Form */}
                        <div className="lg:col-span-2">
                            <div className="border border-[#2A2A2A] rounded-lg p-6 lg:p-10 bg-[#141414]">
                                <h2 style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }} className="text-2xl lg:text-3xl font-light text-[#F5F0EB] mb-8">
                                    Send Us a Message
                                </h2>

                                {submitted ? (
                                    <div className="text-center py-12">
                                        <div className="w-20 h-20 bg-[#C9A96E] rounded-full flex items-center justify-center mx-auto mb-6">
                                            <Send size={40} className="text-[#0A0A0A]" />
                                        </div>
                                        <h3 style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }} className="text-2xl font-light text-[#F5F0EB] mb-3">Message Sent</h3>
                                        <p className="text-[#8A8A7A]">
                                            Thank you for reaching out. We shall respond within 24 hours.
                                        </p>
                                    </div>
                                ) : (
                                    <form onSubmit={handleSubmit} className="space-y-6">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                            <div>
                                                <label className="block text-sm font-medium text-[#F5F0EB] mb-2 tracking-wide">Your Name *</label>
                                                <input
                                                    type="text"
                                                    name="name"
                                                    value={formData.name}
                                                    onChange={handleInputChange}
                                                    required
                                                    className="w-full px-4 py-3 bg-[#1A1A1A] border border-[#2A2A2A] rounded-lg text-[#F5F0EB] placeholder-[#8A8A7A] focus:outline-none focus:border-[#C9A96E] transition-colors"
                                                    placeholder="Your full name"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-[#F5F0EB] mb-2 tracking-wide">Email Address *</label>
                                                <input
                                                    type="email"
                                                    name="email"
                                                    value={formData.email}
                                                    onChange={handleInputChange}
                                                    required
                                                    className="w-full px-4 py-3 bg-[#1A1A1A] border border-[#2A2A2A] rounded-lg text-[#F5F0EB] placeholder-[#8A8A7A] focus:outline-none focus:border-[#C9A96E] transition-colors"
                                                    placeholder="your@email.com"
                                                />
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                            <div>
                                                <label className="block text-sm font-medium text-[#F5F0EB] mb-2 tracking-wide">Phone Number</label>
                                                <input
                                                    type="tel"
                                                    name="phone"
                                                    value={formData.phone}
                                                    onChange={handleInputChange}
                                                    className="w-full px-4 py-3 bg-[#1A1A1A] border border-[#2A2A2A] rounded-lg text-[#F5F0EB] placeholder-[#8A8A7A] focus:outline-none focus:border-[#C9A96E] transition-colors"
                                                    placeholder="+91 98765 43210"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-[#F5F0EB] mb-2 tracking-wide">Subject *</label>
                                                <select
                                                    name="subject"
                                                    value={formData.subject}
                                                    onChange={handleInputChange}
                                                    required
                                                    className="w-full px-4 py-3 bg-[#1A1A1A] border border-[#2A2A2A] rounded-lg text-[#F5F0EB] focus:outline-none focus:border-[#C9A96E] transition-colors appearance-none"
                                                    style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%238A8A7A' d='M6 8L1 3h10z'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 16px center' }}
                                                >
                                                    <option value="" style={{ backgroundColor: '#1A1A1A' }}>Select a subject</option>
                                                    <option value="general" style={{ backgroundColor: '#1A1A1A' }}>General Inquiry</option>
                                                    <option value="custom" style={{ backgroundColor: '#1A1A1A' }}>Bespoke Design Request</option>
                                                    <option value="order" style={{ backgroundColor: '#1A1A1A' }}>Order Support</option>
                                                    <option value="collaboration" style={{ backgroundColor: '#1A1A1A' }}>Collaboration</option>
                                                    <option value="wholesale" style={{ backgroundColor: '#1A1A1A' }}>Wholesale Inquiry</option>
                                                    <option value="bridal" style={{ backgroundColor: '#1A1A1A' }}>Bridal Consultation</option>
                                                </select>
                                            </div>
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-[#F5F0EB] mb-2 tracking-wide">Message *</label>
                                            <textarea
                                                name="message"
                                                value={formData.message}
                                                onChange={handleInputChange}
                                                required
                                                rows="6"
                                                className="w-full px-4 py-3 bg-[#1A1A1A] border border-[#2A2A2A] rounded-lg text-[#F5F0EB] placeholder-[#8A8A7A] focus:outline-none focus:border-[#C9A96E] transition-colors resize-none"
                                                placeholder="Tell us about your inquiry or bespoke requirements..."
                                            />
                                        </div>

                                        <button
                                            type="submit"
                                            className="w-full flex items-center justify-center gap-2 px-8 py-4 bg-[#C9A96E] text-[#0A0A0A] font-semibold rounded-lg hover:bg-[#D4B87A] transition-colors tracking-wide"
                                        >
                                            <Send size={20} />
                                            Send Message
                                        </button>
                                    </form>
                                )}
                            </div>
                        </div>

                        {/* Contact Information */}
                        <div className="space-y-6">
                            {/* Contact Details Card */}
                            <div className="border border-[#2A2A2A] rounded-lg p-6 bg-[#141414]">
                                <h3 style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }} className="text-xl font-light text-[#F5F0EB] mb-6">
                                    Contact Information
                                </h3>
                                <div className="space-y-5">
                                    <div className="flex items-start gap-4">
                                        <div className="w-12 h-12 bg-[#1A1A1A] border border-[#2A2A2A] rounded-full flex items-center justify-center flex-shrink-0">
                                            <Mail className="text-[#C9A96E]" size={20} />
                                        </div>
                                        <div>
                                            <h4 className="font-medium text-[#F5F0EB] mb-1">Email</h4>
                                            <a href="mailto:atelier@noir.com" className="text-[#8A8A7A] hover:text-[#C9A96E] transition-colors">
                                                atelier@noir.com
                                            </a>
                                        </div>
                                    </div>

                                    <div className="flex items-start gap-4">
                                        <div className="w-12 h-12 bg-[#1A1A1A] border border-[#2A2A2A] rounded-full flex items-center justify-center flex-shrink-0">
                                            <Phone className="text-[#C9A96E]" size={20} />
                                        </div>
                                        <div>
                                            <h4 className="font-medium text-[#F5F0EB] mb-1">Phone</h4>
                                            <a href="tel:+919876543210" className="text-[#8A8A7A] hover:text-[#C9A96E] transition-colors">
                                                +91 98765 43210
                                            </a>
                                        </div>
                                    </div>

                                    <div className="flex items-start gap-4">
                                        <div className="w-12 h-12 bg-[#1A1A1A] border border-[#2A2A2A] rounded-full flex items-center justify-center flex-shrink-0">
                                            <MessageCircle className="text-[#C9A96E]" size={20} />
                                        </div>
                                        <div>
                                            <h4 className="font-medium text-[#F5F0EB] mb-1">WhatsApp</h4>
                                            <a href="https://wa.me/919876543210" target="_blank" rel="noopener noreferrer" className="text-[#8A8A7A] hover:text-[#C9A96E] transition-colors">
                                                +91 98765 43210
                                            </a>
                                        </div>
                                    </div>

                                    <div className="flex items-start gap-4">
                                        <div className="w-12 h-12 bg-[#1A1A1A] border border-[#2A2A2A] rounded-full flex items-center justify-center flex-shrink-0">
                                            <MapPin className="text-[#C9A96E]" size={20} />
                                        </div>
                                        <div>
                                            <h4 className="font-medium text-[#F5F0EB] mb-1">Atelier</h4>
                                            <p className="text-[#8A8A7A]">
                                                123 Couture Lane<br />
                                                Bandra West, Mumbai 400050<br />
                                                Maharashtra, India
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Business Hours */}
                            <div className="bg-[#141414] border border-[#2A2A2A] rounded-lg p-6">
                                <div className="flex items-center gap-3 mb-5">
                                    <Clock size={22} className="text-[#C9A96E]" />
                                    <h3 style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }} className="text-xl font-light text-[#F5F0EB]">Business Hours</h3>
                                </div>
                                <div className="space-y-3 text-sm">
                                    <div className="flex justify-between">
                                        <span className="text-[#8A8A7A]">Monday - Friday</span>
                                        <span className="font-medium text-[#F5F0EB]">10:00 AM - 7:00 PM</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-[#8A8A7A]">Saturday</span>
                                        <span className="font-medium text-[#F5F0EB]">11:00 AM - 5:00 PM</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-[#8A8A7A]">Sunday</span>
                                        <span className="font-medium text-[#F5F0EB]">By Appointment</span>
                                    </div>
                                </div>
                            </div>

                            {/* Map Placeholder */}
                            <div className="bg-[#141414] border border-[#2A2A2A] rounded-lg p-6 h-48 flex flex-col items-center justify-center">
                                <MapPin size={32} className="text-[#C9A96E] mb-3" />
                                <p style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }} className="text-[#F5F0EB] text-lg font-light">Bandra West, Mumbai</p>
                                <p className="text-[#8A8A7A] text-sm mt-1">Maharashtra, India</p>
                            </div>
                        </div>
                    </div>

                    {/* FAQ Section */}
                    <div className="mt-16 lg:mt-24">
                        <h2 style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }} className="text-3xl lg:text-4xl font-light text-center text-[#F5F0EB] mb-12">
                            Common Questions
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {[
                                { q: 'Do you offer bespoke designs?', a: 'Absolutely. We specialise in creating one-of-a-kind bespoke pieces tailored to your vision. Book a private consultation to discuss your requirements.' },
                                { q: 'What is the turnaround time for custom orders?', a: 'Bespoke creations typically require 4-6 weeks. Bridal and heavily embellished pieces may take 8-12 weeks. Rush orders are available upon request.' },
                                { q: 'Do you ship internationally?', a: 'Yes, we ship to over 30 countries worldwide. International orders are carefully packaged and insured for safe delivery.' },
                                { q: 'Can I visit the atelier?', a: 'We welcome atelier visits by appointment only. Please contact us to schedule a private viewing and consultation.' },
                            ].map((faq, idx) => (
                                <div key={idx} className="border border-[#2A2A2A] rounded-lg p-6 bg-[#141414] hover:border-[#C9A96E] transition-colors">
                                    <h4 style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }} className="text-lg font-medium text-[#F5F0EB] mb-2">{faq.q}</h4>
                                    <p className="text-[#8A8A7A] leading-relaxed text-sm">{faq.a}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* WhatsApp Floating Button */}
                <a
                    href="https://wa.me/919876543210"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-[#C9A96E] hover:bg-[#D4B87A] rounded-full flex items-center justify-center shadow-lg shadow-[#C9A96E]/20 transition-all hover:scale-110"
                    aria-label="Chat on WhatsApp"
                >
                    <MessageCircle size={26} className="text-[#0A0A0A]" />
                </a>

                <Footer />
            </div>
        </>
    );
};

export default Contact21;
